import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ILogger, LOGGER_TOKEN } from './logger/ilogger';
import { ThemePreferenceService } from './theme-preference.service';

export interface LocalLoginResponse {
  token: string;
  tokenType: string;
}

export interface WitnessTokenResponse {
  witnesstoken: string;
  createdAt: string;
  witnessName: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly logger = inject<ILogger>(LOGGER_TOKEN);
  private http = inject(HttpClient);
  private themePreferenceService = inject(ThemePreferenceService);
  router = inject(Router);

  public user: any = null;
  private witnessToken = '';
  public witnessTokenCreatedAt = null;
  public witnessName = null;
  public selectedRegions = "All";
  public selectedCampuses = "All";
  public selectedFacilities = "All";
  public selectedLocationGroups = "All";
  public isFirstOidcToken = true;
  public pincodeUpdatedOnThisSession = false;
  public userSessionTimedOut = new BehaviorSubject<boolean>(false);
  public deIdentified = false;
  private _pendoInitialized = false;
  public userInitialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public userAuthFailed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public localSessionAuthenticated = new BehaviorSubject<boolean>(false);
  private localSessionInitialization?: Promise<boolean>;

  get pendoInitialized(): boolean {
    return this._pendoInitialized;
  }

  setPendoInitialized(value: boolean): void {
    this._pendoInitialized = value;
  }


  public setUser(user: any) {
    this.user = user;
    this.themePreferenceService.applyPreference(user?.themePreference);
    if (this.isDemoUser()) {
      this.deIdentified = true;
      sessionStorage.setItem('deIdentified', 'true');
    }
  }

  public getUserDisplayName(): string {
    return this.user?.fullname || this.user?.email || '';
  }

  public clearCurrentTabSession(): void {
    this.user = null;
    this.deIdentified = false;
    this.pincodeUpdatedOnThisSession = false;
    this.deleteWitnesToken();
    this.resetLocationData();
    this.userInitialized.next(false);
    this.localSessionAuthenticated.next(false);
    sessionStorage.removeItem('local-access-token');
    this.localSessionInitialization = undefined;
  }

  public loginUser(username: string, password: string): Observable<LocalLoginResponse> {
    return this.http.post<LocalLoginResponse>(environment.apiUrl + "login", { "username": username, "password": password });
  }

  public startLocalSession(token: string): void {
    if (!environment.enableLocalTestAuth) {
      throw new Error('Local test authentication is disabled');
    }
    sessionStorage.setItem('local-access-token', token);
  }

  public getLocalAccessToken(): string | null {
    return environment.enableLocalTestAuth ? sessionStorage.getItem('local-access-token') : null;
  }

  public hasLocalSession(): boolean {
    return Boolean(this.getLocalAccessToken());
  }

  public initializeLocalSession(): Promise<boolean> {
    if (!this.hasLocalSession()) {
      return Promise.resolve(false);
    }
    if (this.user) {
      return Promise.resolve(true);
    }
    if (!this.localSessionInitialization) {
      this.localSessionInitialization = firstValueFrom(this.getUserAccount())
        .then((user) => {
          this.setUser(user);
          this.userInitialized.next(true);
          this.localSessionAuthenticated.next(true);
          return true;
        })
        .catch((error) => {
          this.logger.exception(error);
          this.clearCurrentTabSession();
          return false;
        });
    }
    return this.localSessionInitialization;
  }

  public updatePinCode(pincode: any){
    return this.http.post(environment.apiUrl + "user/update/pincode", { "pincode":pincode });
  }

  public getUserAccount() {
    return this.http.get(environment.apiUrl + "user/me");
  }

  public loginWitness(data: any) {
    return this.http.post(environment.apiUrl + "witness/login", data);
  }

  public loginAuthorizedWitness(): Observable<WitnessTokenResponse> {
    return this.http.post<WitnessTokenResponse>(environment.apiUrl + "witness/authorized", {});
  }

  public setWitnesToken(data: any) {
    this.witnessTokenCreatedAt = data.createdAt;
    this.witnessToken = data.witnesstoken;
    this.witnessName = data.witnessName;
  }

  public getWitnessToken() {
    return this.witnessToken;
  }

  public resetLocationData() {
    this.selectedRegions = "All";
    this.selectedFacilities = "All";
    this.selectedCampuses = "All";
    this.selectedLocationGroups = "All"
  }

  public redirectToErrorByUrl(url: string){
    
    if(url.includes("user/me")){
      const id = 1001; //i.e. user profile lookup failed
      this.router.navigate(['/error-handler', id]);
    }
  }

  public redirectToErrorByID(id: number){
    this.router.navigate(['/error-handler', id]);
  }

  public getSetLocalUserAccount() {
    // Try to get from localStorage first
    const localUser = localStorage.getItem('user');
    if (localUser) {
      this.user = JSON.parse(localUser);
      if (this.isDemoUser()) {
        this.deIdentified = true;
        sessionStorage.setItem('deIdentified', 'true');
      }
    }
    // Then get from API to ensure latest data
    return new Promise((resolve) => {
      this.getUserAccount().subscribe({
        next: (result) => {
          this.setUser(result);
          if (this.isDemoUser()) {
            this.deIdentified = true;
            sessionStorage.setItem('deIdentified', 'true');
          }
          this.userInitialized.next(true);
          resolve(this.user);
        },
        error: (error) => {
          this.logger.exception(error);
          this.userInitialized.next(true);
          if (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 400 || error.status === 401)) {
            this.userAuthFailed.next(true);
          }
          resolve(this.user);
        }
      });
    });
  }

  /** MP-116 */
  public deleteWitnesToken() {
    this.witnessTokenCreatedAt = null;
    this.witnessToken = '';
    this.witnessName = null;
  }

  public getActiveUsers(){
    return this.http.get(environment.apiUrl+'get/active_users')
  }

  isDemoUser(): boolean {
    return this.user?.deIdUser;
  }

  getDeIdentifiedMode(): boolean {
    return this.isDemoUser() ? true : this.deIdentified;
  }

  setDeIdentified(value: boolean): void {
    if (!this.isDemoUser()) {
      this.deIdentified = value;
    }
  }

  getDeIdState(): { value: boolean, disabled: boolean } {
    const isDemo = this.isDemoUser();
    return {
      value: isDemo || this.deIdentified,
      disabled: isDemo
    };
  }

}
